"use client";
import { ResourceHoursHelpTooltip } from "@/features/settings/components/hours/ResourceHoursHelpTooltip";
import { CreateOrEditHourRule } from "@/features/settings/components/hours/rule/CreateOrEditHourRule";
import { useBulkHourRules } from "@/features/settings/hooks/useHours";
import { api } from "@/lib/api/api";
import {
  IHour,
  IResource,
  Office,
  cover24Hours,
  parseDateOnlyString,
  parseTimeOnly,
  prettyTimeOnly
} from "@gettactic/api";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import { ExternalLink } from "@cmp-src/primitives/external-link";
import { DataMan } from "@gettactic/components/src/data-display/data-man/DataMan";
import { EmptyView } from "@gettactic/components/src/data-display/EmptyView/EmptyView";
import { actionEdit } from "@gettactic/components/src/data-display/data-man/default-actions/actionEdit";
import { actionDelete } from "@gettactic/components/src/data-display/data-man/default-actions/actionDelete";
import { useDisclosure } from "@gettactic/helpers";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Clock } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import { IoAdd as Plus } from "react-icons/io5";
import toast from "react-hot-toast";
import { SectionContentWrapper } from "@cmp-src/layout/SectionContentWrapper";

type Props = {
  office: Office;
  resources?: IResource[];
  title: string;
};

export type IHourWithResName = IHour & {
  resName?: string;
};

export function groupSharedKey(hour: IHourWithResName) {
  return `${hour.is_available ? "true" : "false"}_${hour.since}_${hour.until}_${hour.start_time}_${hour.end_time}_${
    hour.name
  }`;
}

export type ConfirmOperationModal = {
  operation: "delete" | "edit";
  applyTo: IHourWithResName[];
};

export function ListHourRules({ title, office, resources }: Props) {
  const queryClient = useQueryClient();
  const formDisclosure = useDisclosure<IHourWithResName[]>();
  const hours = useBulkHourRules(office, resources);

  const invalidateQuery = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: ["hours.rules", office.id]
      }),
    [office, queryClient]
  );

  const [allHours, inGroups] = useMemo(() => {
    const data = hours.data ?? [];
    const inGroups: { [idx: string]: IHourWithResName[] } = {};
    const allHours: IHourWithResName[] = [];

    data.forEach((group) => {
      if (group) {
        group.elements.forEach((hour) => {
          const res = (resources ?? []).find((x) => x.id === hour.resource_id);
          const hourWithResName: IHourWithResName = {
            ...hour,
            resName: res?.name ?? "-"
          };
          inGroups[groupSharedKey(hour)] = inGroups[groupSharedKey(hour)] || [];
          inGroups[groupSharedKey(hour)].push(hourWithResName);
          allHours.push(hourWithResName);
        });
      }
    });

    return [allHours, inGroups];
  }, [hours, resources]);

  const columns = React.useMemo(
    () => [
      {
        key: "name",
        title: "Name",
        render: (data: IHourWithResName) => (
          <div
            title={data.name ?? undefined}
            className="max-w-[120px] overflow-hidden text-ellipsis text-sm"
          >
            {data.name}
          </div>
        )
      },
      ...(resources
        ? [
            {
              key: "resName",
              title: "Resource",
              render: (data: IHourWithResName) => (
                <div className="text-sm">{data.resName}</div>
              )
            }
          ]
        : []),
      {
        key: "dates",
        title: "Date",
        render: (data: IHourWithResName) => {
          const since = data.since ? parseDateOnlyString(data.since) : null;
          const until = data.until ? parseDateOnlyString(data.until) : null;
          const sinceShort = since ? format(since, "M/dd/yy") : null;
          const untilShort = until ? format(until, "M/dd/yy") : null;
          return (
            <div className="text-sm">
              {!since && !until ? (
                <span className="font-bold">Always</span>
              ) : since && sinceShort === untilShort ? (
                <span>{sinceShort}</span>
              ) : (
                <>
                  <span>{sinceShort}</span>
                  <span> - </span>
                  <span>{untilShort}</span>
                </>
              )}
            </div>
          );
        }
      },
      {
        key: "hours",
        title: "Hours",
        render: (data: IHourWithResName) => {
          const isAllDay = cover24Hours(
            parseTimeOnly(data.start_time),
            parseTimeOnly(data.end_time)
          );
          return (
            <div className="text-sm">
              {isAllDay ? (
                <span>All day</span>
              ) : (
                <>
                  <span>{prettyTimeOnly(data.start_time)}</span>
                  <span> - </span>
                  <span>{prettyTimeOnly(data.end_time)}</span>
                </>
              )}
            </div>
          );
        }
      },
      {
        key: "is_available",
        title: "Availability",
        render: (data: IHourWithResName) => (
          <div className="text-sm">
            {data.is_available ? (
              <span className="inline-block rounded-full bg-green-600 px-2 py-1 text-xs text-white">
                Open
              </span>
            ) : (
              <span className="inline-block rounded-full bg-red-200 px-2 py-1 text-xs text-red-900">
                Closed
              </span>
            )}
          </div>
        )
      }
    ],
    [resources]
  );

  const actions = React.useMemo(
    () => [
      actionEdit(({ target }) => {
        const hour = target as IHourWithResName;
        const group = inGroups[groupSharedKey(hour)] || [hour];
        formDisclosure.open(group);
      }),
      actionDelete(
        async ({ target }) => {
          const hour = target as IHourWithResName;
          const group = inGroups[groupSharedKey(hour)] || [hour];
          try {
            await Promise.all(
              group.map((h) => api.client.hours.delete(office.id, h.id))
            );
            toast.success("Hours deleted");
            invalidateQuery();
          } catch (error) {
            toast.error("Failed to delete hours");
            throw error;
          }
        },
        {
          label: "Delete Hours",
          confirm: {
            title: "Delete Hours",
            message: "Are you sure you want to delete these hours?"
          }
        }
      )
    ],
    [inGroups, office.id, invalidateQuery, formDisclosure]
  );

  const settingsTitle = (
    <>
      {title} {resources ? <ResourceHoursHelpTooltip /> : null}
    </>
  );

  const description = !resources ? (
    <>
      Define any holidays or office closures for this location. Reservations
      cannot be made when an office is closed.{" "}
      <ExternalLink href="https://gettactic.zendesk.com/hc/en-us/articles/27811185241371-Manage-Office-Hours">
        <span>Learn more</span>{" "}
        <ExternalLinkIcon className="inline-flex h-5 w-5" />
      </ExternalLink>
    </>
  ) : undefined;

  const emptyView = (
    <EmptyView
      icon={Clock}
      title={resources ? "No Custom Hours" : "No Holidays & Closures"}
      message="Get started by adding hours"
      actions={[
        {
          label: "Add Hours",
          onClick: () => formDisclosure.open(),
          icon: Plus
        }
      ]}
    />
  );

  return (
    <>
      <SectionContentWrapper
        title={settingsTitle}
        description={description}
        actions={[
          {
            label: "Add Hours",
            onClick: () => formDisclosure.open(),
            icon: <Plus className="h-4 w-4" />
          }
        ]}
        isLoading={hours.isLoading}
        isEmpty={hours.isSuccess && allHours.length === 0}
        emptyView={emptyView}
        className="col-span-12"
      >
        <DataMan data={allHours} columns={columns} actions={actions} />
      </SectionContentWrapper>

      <CreateOrEditHourRule
        hours={
          Array.isArray(formDisclosure.data) ? formDisclosure.data : undefined
        }
        isOpen={formDisclosure.isOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            formDisclosure.close();
          }
        }}
        office={office}
        resources={resources}
        invalidateQuery={invalidateQuery}
      />
    </>
  );
}
